import styled from 'styled-components';
import type {
  EditorialQuote as EditorialQuoteSchema,
  QuotationMarks,
  TextTransform,
} from '@amplience/content-types/typings/c-editorial-quote';
import { ClosingQuote, OpeningQuote } from '@storefront/lib-global/icons';
import { RichTextPartial } from '@storefront/lib-global/partials/richText';
import { useTextTransform } from '@storefront/lib-global/partials/textTransform';
import { TypographyStyles, colours, maxWidthPartial, media, spacing } from '@storefront/lib-global/stylings';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';
import type { QuotationMarkSizesType } from '../../utils';
import { quotationMarkSizes } from '../../utils';

export type EditorialQuoteProps = EditorialQuoteSchema;

const S = {
  Container: styled.div`
    ${maxWidthPartial({ withPaddingXL: true })}
  `,
  Quote: styled.blockquote<{
    $backgroundColor: string;
    $quotationMarks?: QuotationMarks;
  }>`
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    display: flex;
    justify-content: center;
    margin: 0;
    padding: ${spacing.L} ${spacing.S};
    position: relative;

    svg {
      transform: ${({ $quotationMarks }) =>
        `scale(${quotationMarkSizes[($quotationMarks?.size?.mobile ?? 'Default') as QuotationMarkSizesType]})`};
    }

    svg > path {
      fill: ${({ $quotationMarks }) => $quotationMarks?.color};
    }

    @media ${media.greaterThan('md')} {
      padding: ${spacing.L} 70px;
    }

    @media ${media.greaterThan('lg')} {
      padding: 60px 185px;

      svg {
        transform: ${({ $quotationMarks }) =>
          `scale(${quotationMarkSizes[($quotationMarks?.size?.desktop ?? 'Default') as QuotationMarkSizesType]})`};
      }

      svg > path {
        fill: ${({ $quotationMarks }) => $quotationMarks?.color};
      }
    }
  `,

  OpeningQuoteContainer: styled.div`
    left: 6vw;
    position: absolute;
    top: -16px;

    @media ${media.greaterThan('lg')} {
      top: -10px;
    }
  `,

  ClosingQuoteContainer: styled.div`
    bottom: -22px;
    position: absolute;
    right: 7vw;

    @media ${media.greaterThan('lg')} {
      bottom: -15px;
    }
  `,

  Text: styled(RichTextPartial)<{ $textColor: string; $textTransform?: TextTransform }>`
    ${({ $textTransform = 'UPPERCASE' }) => useTextTransform($textTransform)}
    color: ${({ $textColor }) => $textColor};
    margin: 0;
    overflow-wrap: anywhere;

    * {
      margin: 0;
    }

    a {
      color: ${({ $textColor }) => $textColor};
    }
  `,
};

function EditorialQuoteText({ editorialQuote }: Partial<EditorialQuoteProps>) {
  const text = getLocalizedValue(editorialQuote?.text?.richText);

  return (
    <S.Text
      text={text}
      fontSize={TypographyStyles.Headings.H2}
      $textColor={editorialQuote?.textColor ?? colours.BLACK}
      $textTransform={editorialQuote?.textTransform ?? 'UPPERCASE'}
    />
  );
}

export function EditorialQuote({ editorialQuote, quotationMarks, backgroundColor }: EditorialQuoteProps) {
  return (
    <S.Container>
      <S.Quote $backgroundColor={backgroundColor} $quotationMarks={quotationMarks}>
        <S.OpeningQuoteContainer>
          <OpeningQuote />
        </S.OpeningQuoteContainer>
        <EditorialQuoteText editorialQuote={editorialQuote} />
        <S.ClosingQuoteContainer>
          <ClosingQuote />
        </S.ClosingQuoteContainer>
      </S.Quote>
    </S.Container>
  );
}

export default EditorialQuote;
